import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UpdateMobilityEmailContentDocument,
    type UpdateMobilityEmailContentMutation,
    type UpdateMobilityEmailContentMutationVariables,
} from '../../../../api/mutations/updateMobilityEmailContent';
import { EmailContentUpdateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import MobilityEmailScenarioContent from './MobilityEmailScenarioContent';
import {
    type MobilityEmailContentFormProps,
    type MobilityEmailContentFormikValue,
    getMobilityEmailContentFromForm,
} from './shared';
import useHandleMobilityEmailAsset from './useHandleMobilityEmailAsset';

const MobilityEmailContentForm = ({ module }: MobilityEmailContentFormProps) => {
    const apolloClient = useApolloClient();
    const { t } = useTranslation('mobilityModuleDetails');
    const mobilityAsset = useHandleMobilityEmailAsset();

    const initialValues: MobilityEmailContentFormikValue = useMemo(
        () => ({
            emailContents: module.mobilityEmailContents,
        }),
        [module.mobilityEmailContents]
    );

    const onSubmit = useHandleError<MobilityEmailContentFormikValue>(
        async values => {
            // submitting message
            message.loading({
                content: t('mobilityModuleDetails:messages.updateMobilityDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient.mutate<UpdateMobilityEmailContentMutation, UpdateMobilityEmailContentMutationVariables>({
                mutation: UpdateMobilityEmailContentDocument,
                variables: {
                    moduleId: module.id,
                    emailContents: getMobilityEmailContentFromForm(values),
                    emailContentUpdateType: EmailContentUpdateType.Module,
                },
            });

            await mobilityAsset(module.id, values, EmailContentUpdateType.Module, initialValues);

            // inform about success
            message.success({
                content: t('mobilityModuleDetails:messages.updateMobilityDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, initialValues, mobilityAsset, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
            {({ handleSubmit }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <MobilityEmailScenarioContent
                        companyId={module.companyId}
                        defaultValueDisabled={false}
                        emailContents={module.mobilityEmailContents}
                        isHomeDeliveryEnabled={module.homeDelivery.isEnable}
                        locations={module.locations}
                        sendEmailToCustomer={module.sendEmailToCustomer}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default MobilityEmailContentForm;
